import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, Mail, ShieldAlert } from "lucide-react";
import { motion } from "framer-motion";
import api, { formatApiError } from "../lib/api";
import { AuthHeroPanel } from "../components/AuthHeroPanel";

const E1_LOGO =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_err-dashboard-portal/artifacts/phsk29o5_E1-Primary-Logo_Small_Transparent.png";

export default function ForgotPasswordPage() {
  const [usernameOrEmail, setUsernameOrEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const submitRequest = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", {
        username_or_email: usernameOrEmail,
      });
      setSubmitted(true);
    } catch (requestError) {
      setError(
        formatApiError(requestError.response?.data?.detail) ||
          "We could not send a recovery link. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen grid grid-cols-1 lg:grid-cols-5"
      data-testid="forgot-password-page"
    >
      <AuthHeroPanel testId="forgot-password-panel" logoTestId="forgot-password-brand"
        eyebrow="Secure account recovery"
        footer={<><Mail size={14} /> Recovery link · Single use · 60 minutes</>}>
        <h1 className="font-heading text-5xl font-light leading-none xl:text-6xl">
          Regain access.<br />
          <span className="font-black">Stay protected.</span>
        </h1>
      </AuthHeroPanel>

      <main
        className="col-span-1 flex items-center justify-center px-6 py-12 md:px-16 lg:col-span-3"
      >
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <img
            src={E1_LOGO}
            alt="EmployeesFirst"
            className="mb-10 h-12 max-w-[260px] w-auto object-contain lg:hidden"
            width="260"
            height="32"
            data-testid="forgot-password-mobile-logo"
          />
          <Link
            to="/login"
            className="mb-10 inline-flex items-center gap-2 text-sm font-semibold"
            style={{ color: "var(--ink)" }}
            data-testid="forgot-password-back-link"
          >
            <ArrowLeft size={16} /> Back To Sign In
          </Link>

          <div className="eyebrow mb-3">Password recovery</div>
          <h2
            className="font-heading text-4xl font-black leading-none md:text-5xl"
            style={{ color: "var(--ink)" }}
            data-testid="forgot-password-heading"
          >
            Reset your access.
          </h2>

          {submitted ? (
            <div
              className="mt-8 border p-6"
              style={{ borderColor: "var(--border-strong)" }}
            >
              <CheckCircle2 size={24} style={{ color: "var(--accent)" }} />
              <p
                className="mt-4 text-base font-semibold"
                style={{ color: "var(--ink)" }}
                data-testid="forgot-password-confirmation"
              >
                If an active account matches that information, a recovery link is on its way.
              </p>
              <p
                className="mt-2 text-sm leading-relaxed"
                style={{ color: "var(--muted)" }}
              >
                Check your registered work email. The link expires after 60 minutes and can be
                used once.
              </p>
              <button
                type="button"
                className="btn-ghost mt-6 inline-flex items-center gap-2"
                onClick={() => setSubmitted(false)}
                data-testid="forgot-password-send-another-button"
              >
                Send Another Link <ArrowRight size={16} />
              </button>
            </div>
          ) : (
            <>
              <p className="mb-10 mt-3 text-sm" style={{ color: "var(--muted)" }}>
                Enter your username or registered work email. We will send a secure recovery link if
                an active account matches.
              </p>

              {error && (
                <div
                  className="pill-danger mb-6 flex items-start gap-2 p-3 text-sm"
                  data-testid="forgot-password-error"
                >
                  <ShieldAlert size={16} className="mt-0.5" /> <span>{error}</span>
                </div>
              )}

              <form
                onSubmit={submitRequest}
                className="space-y-8"
                data-testid="forgot-password-form"
              >
                <div>
                  <label className="field-label" htmlFor="recovery-identifier">
                    Client username or work email
                  </label>
                  <input
                    id="recovery-identifier"
                    className="input-flat"
                    type="text"
                    value={usernameOrEmail}
                    onChange={(event) => setUsernameOrEmail(event.target.value)}
                    placeholder="j.smith or name@company.com"
                    autoComplete="username"
                    required
                    data-testid="forgot-password-identifier-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary flex w-full items-center justify-center gap-2"
                  data-testid="forgot-password-submit-button"
                >
                  {loading ? (
                    <Loader2 className="animate-spin" size={16} />
                  ) : (
                    <>
                      Send Recovery Link <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
}